import { dolomiteConfigs } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'

/**
 * Ordered marketId list for a chain, from the cached `markets` map in the
 * data-sdk config. `buildLenderCall` is synchronous, so a live `getNumMarkets`
 * round-trip isn't possible here — the published id list is the contract. The
 * parser reads the SAME list so call order and slice math stay in lock-step.
 * (The token VALUE in the map is informational; the parser reads the live token
 * via `getMarketTokenAddress`.)
 */
export const getDolomiteMarketIds = (chainId: string): string[] => {
  const markets = dolomiteConfigs()?.[chainId]?.markets
  return markets ? Object.keys(markets) : []
}

// Per-market granular getters, in slice order. The deployed contract lacks the
// `getMarketWithInfo` mega-struct and the APR getters, so we assemble from
// these stable per-field getters and derive the supply rate in the parser.
export const DOLOMITE_MARKET_FNS = [
  'getMarketTokenAddress',
  'getMarketIsClosing',
  'getMarketTotalPar',
  'getMarketCurrentIndex',
  'getMarketPrice',
  'getMarketMarginPremium',
  'getMarketInterestRate',
] as const

export const DOLOMITE_GLOBAL_FNS = [
  'getMarginRatio',
  'getEarningsRate',
] as const

/**
 * Two global calls (`getMarginRatio`, `getEarningsRate`) then
 * `DOLOMITE_MARKET_FNS.length` calls per market.
 * Slice length = 2 + 7 * marketCount.
 */
export const buildDolomiteCall = (
  chainId: string,
  _lender?: string,
): Call[] => {
  const margin = dolomiteConfigs()?.[chainId]?.dolomiteMargin
  const marketIds = getDolomiteMarketIds(chainId)
  if (!margin || marketIds.length === 0) return []

  const calls: Call[] = DOLOMITE_GLOBAL_FNS.map((name) => ({
    address: margin,
    name,
    params: [],
  }))
  for (const marketId of marketIds) {
    for (const name of DOLOMITE_MARKET_FNS) {
      calls.push({ address: margin, name, params: [marketId] })
    }
  }
  return calls
}
